import React, { useEffect, useState } from "react";
import { api, apiErr } from "../../lib/api";
import { toast } from "sonner";
import { TrendingUp, Euro, Percent, Download, Wallet, Receipt } from "lucide-react";

const MONTHS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
const eur = (n) => new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n || 0) + " €";

export default function FinanceTab() {
  const now = new Date().getFullYear();
  const [year, setYear] = useState(now);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/finance?year=${year}`).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  }, [year]);

  const exportCsv = () => {
    if (!data) return;
    const rows = [["Mois", "Réservations", "Nuits", "Chiffre d'affaires (€)", "Taxe de séjour (€)"]];
    data.months.forEach((m, i) => rows.push([MONTHS[i], m.count, m.nights, m.revenue, m.tourist_tax]));
    rows.push(["TOTAL", data.bookings_count, data.nights_booked, data.revenue_total, data.tourist_tax_total]);
    const csv = rows.map((r) => r.join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `finances-begaudiere-${year}.csv`;
    a.click();
  };

  const maxRev = data ? Math.max(...data.months.map((m) => m.revenue), 1) : 1;
  const cards = data ? [
    ["Chiffre d'affaires", eur(data.revenue_total), Euro],
    ["Acomptes encaissés", eur(data.deposits_collected), Wallet],
    ["Soldes à venir (sur place)", eur(data.pending_balance), TrendingUp],
    ["Taxe de séjour", eur(data.tourist_tax_total), Receipt],
    ["Taux d'occupation", `${data.occupancy_rate} %`, Percent],
    ["Réservations", data.bookings_count, TrendingUp],
  ] : [];

  return (
    <div className="space-y-6" data-testid="finance-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          {[now - 1, now, now + 1].map((y) => (
            <button key={y} onClick={() => setYear(y)} data-testid={`finance-year-${y}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium ${year === y ? "bg-brand text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-brand"}`}>{y}</button>
          ))}
        </div>
        <button onClick={exportCsv} disabled={!data} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50" data-testid="finance-export-csv">
          <Download size={15} /> Export CSV
        </button>
      </div>

      {loading ? <p className="text-ink-400">Chargement…</p> : data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {cards.map(([label, val, Icon], i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <Icon size={20} className="text-brand mb-3" strokeWidth={1.5} />
                <div className="text-2xl font-bold tracking-tight text-slate-900" data-testid={`finance-card-${i}`}>{val}</div>
                <div className="text-xs text-slate-500 mt-1">{label}</div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h3 className="text-lg font-bold tracking-tight text-slate-900 mb-4">Chiffre d'affaires par mois · {year}</h3>
            <div className="space-y-2">
              {data.months.map((m, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="w-10 text-xs text-slate-400">{MONTHS[i]}</span>
                  <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                    <div className="h-full bg-brand rounded-full transition-[width] duration-500" style={{ width: `${(m.revenue / maxRev) * 100}%` }} />
                  </div>
                  <span className="w-24 text-right text-sm text-slate-900 tabular-nums">{eur(m.revenue)}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
